import { useState } from "react";
import useSWR from "swr";
import { swrKey } from "@/lib/swr";
import { ChartBarIcon, CirclePlusIcon, ExternalLinkIcon, FileTextIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Chip } from "@/components/badge/chip";
import { RowCard } from "@/components/list/row-card";
import { UserAvatar } from "@/components/user-chip";
import { RowIndex } from "@/components/list/row-index";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { RefreshButton } from "@/components/list/refresh-button";
import { TabBar } from "@/components/nav/route-tabs";
import { Tabs, TabsTrigger } from "@/components/ui/tabs";
import { clientApps, useApps } from "@/hooks/apps";
import { useTabParam } from "@/hooks/tab-param";
import {
  fmtRcDate,
  RC_COUNTRIES,
  rcStageBadge,
  RC_TYPES,
  RcRow,
  RcStats,
  rcConclusionTone,
} from "@/constants/rc";
import { buildArpuLink } from "./rc-arpu";
import { RcFormDialog, refreshRcs } from "./rc-form-dialog";
import { countSuffix } from "@/lib/utils";

const PAGE_SIZE = 50;

// Tabs = the review-flow states (server `stage` filter, derived from
// status + flow tasks). 回收确认/补结论不是独立状态 — pending-close rows
// stay inside 实验中/已部署, conclusion-todo rows inside 已回滚.
const TABS = [
  { key: "approve", label: "待审批" },
  { key: "implement", label: "待配置" },
  { key: "experimenting", label: "实验中" },
  { key: "deployed", label: "已部署" },
  { key: "archived", label: "存档" },
  { key: "closed", label: "已回滚" },
  { key: "all", label: "All" },
] as const;

type TabKey = (typeof TABS)[number]["key"];


const LinkIcon = ({
  href,
  title,
  icon,
}: {
  href?: string | null;
  title: string;
  icon: React.ReactNode;
}) =>
  href ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={title}
      onClick={(e) => e.stopPropagation()}
      className="flex shrink-0 items-center text-muted-foreground hover:text-primary"
    >
      {icon}
    </a>
  ) : null;

// Column captions matching the row slots below (widths kept in sync).
const RcListHeader = ({ showApp }: { showApp: boolean }) => (
  <div className="flex items-center gap-3 px-3 pb-1 text-xs text-muted-foreground">
    <span className="w-8 shrink-0">#</span>
    {/* pl-5 ≈ doc icon width + inner gap, so the caption sits over the text */}
    <span className="min-w-0 flex-1 pl-5">Name</span>
    {/* Narrow screens drop columns by priority (Status stays) so the name
        keeps its room; hidden breakpoints mirror the row slots below. */}
    <span className="hidden w-20 shrink-0 lg:block">需求方</span>
    {showApp && <span className="hidden w-16 shrink-0 xl:block">App</span>}
    <span className="hidden w-16 shrink-0 xl:block">Country</span>
    <span className="hidden w-20 shrink-0 md:block">Type</span>
    <span className="w-24 shrink-0">Status</span>
    <span className="hidden w-36 shrink-0 text-right lg:block">Start → End</span>
  </div>
);

const RcRowCard = ({
  rc,
  n,
  appName,
  showApp,
}: {
  rc: RcRow;
  n: number;
  appName?: string;
  showApp: boolean;
}) => (
  <RowCard to={`/rcs/${rc.id}`}>
    <RowIndex n={n} />
    {/* Leading group: doc icon · conclusion chip · name, tight gap so the
        name doesn't drift right (row-level gap-3 applies only once). */}
    <span className="flex min-w-0 flex-1 items-center gap-1.5">
      {rc.docLink ? (
        <LinkIcon
          href={rc.docLink}
          title="RC doc"
          icon={<FileTextIcon className="h-3.5 w-3.5" />}
        />
      ) : (
        // In-app config doc has no URL — the row itself opens it, so just
        // signal presence instead of dimming the icon.
        <FileTextIcon
          className={`h-3.5 w-3.5 shrink-0 ${
            rc.configDoc ? "text-muted-foreground" : "text-muted-foreground/30"
          }`}
        />
      )}
      {rc.conclusion && (
        <Chip tone={rcConclusionTone(rc.conclusion)}>
          {rc.conclusion}
        </Chip>
      )}
      <span className="min-w-0 truncate font-medium">{rc.name}</span>
    </span>
    <LinkIcon
      href={rc.rcFirebaseLink}
      title="Experiment (Firebase console)"
      icon={<ExternalLinkIcon className="h-3.5 w-3.5" />}
    />
    <LinkIcon
      href={buildArpuLink(rc)}
      title="ARPU dashboard (Friday)"
      icon={<ChartBarIcon className="h-3.5 w-3.5" />}
    />
    <span className="hidden w-20 shrink-0 items-center gap-1.5 lg:flex">
      {rc.requester ? (
        <>
          <UserAvatar user={rc.requester} className="h-5 w-5" />
          <span className="min-w-0 truncate text-xs text-muted-foreground">
            {rc.requester.name}
          </span>
        </>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )}
    </span>
    {showApp && (
      <span className="hidden w-16 shrink-0 items-center xl:flex">
        <Chip tone="neutral" className="max-w-full truncate">
          {appName ?? rc.appId}
        </Chip>
      </span>
    )}
    <span
      title={(rc.countries ?? []).join(", ")}
      className="hidden w-16 shrink-0 truncate text-xs text-muted-foreground xl:block"
    >
      {(rc.countries ?? []).join(",") || "—"}
    </span>
    <span className="hidden w-20 shrink-0 items-center md:flex">
      <Chip tone={RC_TYPES[rc.type]?.tone ?? "neutral"}>
        {RC_TYPES[rc.type]?.label ?? rc.type}
      </Chip>
    </span>
    <span className="flex w-24 shrink-0 items-center">
      <Chip tone={rcStageBadge(rc).tone}>{rcStageBadge(rc).label}</Chip>
    </span>
    <span className="hidden w-36 shrink-0 text-right text-xs tabular-nums text-muted-foreground lg:block">
      {fmtRcDate(rc.startDate)}
      {" → "}
      {fmtRcDate(rc.endDate)}
    </span>
  </RowCard>
);

// RC config list. Standalone page (all visible apps) and app tab (pid fixed)
// share this component; the Feishu views map to the preset tabs + filters.
export const RcList = ({ pid }: { pid?: string }) => {
  const apps = useApps();
  const appNameById = new Map<string, string>(
    apps.map((a) => [String(a.id), a.name ?? ""])
  );
  // 落地页默认停在待审批(审批人从飞书跳回的高频入口);All 兜底放最后
  const [tab, setTab] = useTabParam("tab", "approve");
  const [appFilter, setAppFilter] = useState("");
  const [type, setType] = useState("");
  const [country, setCountry] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const effectivePid = pid || appFilter || undefined;

  const { data, isLoading } = useSWR<{ list: RcRow[]; total: number }>(
    swrKey("/remote-configs", {
      pid: effectivePid,
      stage: tab === "all" ? undefined : tab,
      type,
      country,
      q,
      page,
      pageSize: PAGE_SIZE,
    }),
    { keepPreviousData: true }
  );
  const { data: stats } = useSWR<RcStats>(
    swrKey("/remote-configs/stats", { pid: effectivePid })
  );

  const rows: RcRow[] = data?.list ?? [];
  const total: number = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const tabCount = (key: TabKey): number | undefined =>
    key === "all" ? stats?.total : stats?.stages?.[key];

  // 切 tab = 换视角:筛选条件清空回默认,避免上个 tab 的筛选悄悄带过来
  const switchTab = (key: string) => {
    setTab(key);
    setAppFilter("");
    setType("");
    setCountry("");
    setQ("");
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-3">
      <Tabs value={tab} onValueChange={switchTab}>
        <TabBar
          options={TABS.map((t) => ({
            value: t.key,
            label: `${t.label}${countSuffix(tabCount(t.key))}`,
          }))}
          value={tab}
          onValueChange={switchTab}
          action={
          <div className="flex items-center gap-2">
            <RcFormDialog
              pid={pid}
              trigger={
                <Button variant="outline" size="sm">
                  <CirclePlusIcon className="h-3.5 w-3.5" /> New RC
                </Button>
              }
            />
            <RefreshButton onClick={refreshRcs} />
          </div>
          }
        >
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key}>
              {t.label}
              {countSuffix(tabCount(t.key))}
            </TabsTrigger>
          ))}
        </TabBar>
      </Tabs>
      <div className="flex flex-wrap items-center gap-2">
        {!pid && (
          <Select
            value={appFilter || "__all"}
            onValueChange={(v) => {
              setAppFilter(v === "__all" ? "" : v);
              setPage(1);
            }}
          >
            <SelectTrigger size="sm" className="w-32 text-xs">
              <SelectValue placeholder="App" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All apps</SelectItem>
              {clientApps(apps).map(
                (a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.name}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        )}
        <Select
          value={type || "__all"}
          onValueChange={(v) => {
            setType(v === "__all" ? "" : v);
            setPage(1);
          }}
        >
          <SelectTrigger size="sm" className="w-32 text-xs">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All types</SelectItem>
            {Object.entries(RC_TYPES).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={country || "__all"}
          onValueChange={(v) => {
            setCountry(v === "__all" ? "" : v);
            setPage(1);
          }}
        >
          <SelectTrigger size="sm" className="w-28 text-xs">
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All countries</SelectItem>
            {RC_COUNTRIES.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={q}
          placeholder="Search name / people…"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="h-7 w-48 text-xs"
        />
      </div>

      {isLoading && !data && <ListLoading />}
      {!isLoading && !rows.length && <ListEmpty>No RC configs.</ListEmpty>}

      {rows.length > 0 && <RcListHeader showApp={!pid} />}

      <div className="flex flex-col gap-1">
        {rows.map((rc, i) => (
          <RcRowCard
            key={rc.id}
            rc={rc}
            n={(page - 1) * PAGE_SIZE + i + 1}
            appName={appNameById.get(rc.appId)}
            showApp={!pid}
          />
        ))}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {total} items · page {page}/{pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default RcList;
